import { useTranslation } from 'react-i18next';

import {
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  R1FactType,
  SpeciesEnum,
} from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardHeader } from '@/components/NCCard/NCCard';
import { SpeciesGradientExplainer } from '@/components/NCExplainers/SpeciesGradientExplainer';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { SpeciesGradientGraphTile } from './GraphTile';
import { SpeciesGradientStatsTile } from './StatsTile';

export const SpeciesGradient = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t(`shared.ncaDetail.details.speciesGradient.labels.${species}`)}>
      <SpeciesGradientCard species={species} />
    </PlotCardWrapper>
  );
};

const SpeciesGradientCard = ({ species }: { species: SpeciesEnum }) => {
  const { t } = useTranslation();

  const factNames = {
    shootDensity: R1FactType.marine_shoot_density,
    shootDensityMin: R1FactType.marine_shoot_density_min,
    shootDensityMax: R1FactType.marine_shoot_density_max,
    seagrassProductivity: R1FactType.marine_seagrass_productivity,
    seagrassProductivityMin: R1FactType.marine_seagrass_productivity_min,
    seagrassProductivityMax: R1FactType.marine_seagrass_productivity_max,
    nurseryHabitat: R1FactType.marine_nursery_habitat,
    nurseryHabitatMin: R1FactType.marine_nursery_habitat_min,
    nurseryHabitatMax: R1FactType.marine_nursery_habitat_max,
    biomass: R1FactType.marine_biomass,
    biomassMin: R1FactType.marine_biomass_min,
    biomassMax: R1FactType.marine_biomass_max,
    diversity: R1FactType.marine_diversity,
    diversityMin: R1FactType.marine_diversity_min,
    diversityMax: R1FactType.marine_diversity_max,
    seaweedMeadowsBenchmark: R1FactType.marine_seaweed_meadows_benchmark_graph,
    seaweedMeadowsBenchmarkText: R1FactType.marine_seaweed_meadows_benchmark_text,
  };

  const facts = usePlotFacts(Object.values(factNames));
  const shootDensityFacts = getSpeciesFacts(facts, factNames.shootDensity, species);
  const seagrassProductivityFacts = getSpeciesFacts(facts, factNames.seagrassProductivity, species);
  const nurseryHabitatFacts = getSpeciesFacts(facts, factNames.nurseryHabitat, species);
  const biomassFacts = getSpeciesFactsArray(facts, factNames.biomass, species);
  const diversityFacts = getSpeciesFactsArray(facts, factNames.diversity, species);
  const meadowsBenchmarkFact = getFactFromFactArray<GraphFact[]>(facts, factNames.seaweedMeadowsBenchmark);
  const meadowsBenchmarkTextFact = getFactFromFactArray<string>(facts, factNames.seaweedMeadowsBenchmarkText, {
    [PlotReportFactLabelNameEnum.species]: species,
  });

  const hasData = [
    shootDensityFacts.value,
    seagrassProductivityFacts.value,
    nurseryHabitatFacts.value,
    biomassFacts.value,
    diversityFacts.value,
    meadowsBenchmarkFact?.value?.at(0)?.value,
    meadowsBenchmarkTextFact?.value,
  ].some((fact) => !isNullish(fact));

  if (!hasData) {
    return null;
  }

  return (
    <NCCard explainer={<SpeciesGradientExplainer species={species} />} data-testid={`species-gradient-${species}-nca`}>
      <NCCardHeader
        title={t(`shared.ncaDetail.details.speciesGradient.labels.${species}`)}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <SpeciesGradientGraphTile
          shootDensityFacts={shootDensityFacts}
          seagrassProductivityFacts={seagrassProductivityFacts}
          nurseryHabitatFacts={nurseryHabitatFacts}
          biomassFacts={biomassFacts}
          diversityFacts={diversityFacts}
          species={species}
        />
        <SpeciesGradientStatsTile
          meadowsBenchmarkFact={meadowsBenchmarkFact}
          meadowsBenchmarkTextFact={meadowsBenchmarkTextFact}
          species={species}
        />
      </NCCard2SectionLayout>
    </NCCard>
  );
};

const getSpeciesFacts = (
  facts: PlotReportFactElement<unknown>[] | undefined,
  factName: R1FactType,
  species: SpeciesEnum,
): {
  min: number | null | undefined;
  max: number | null | undefined;
  value: number | null | undefined;
} => {
  return {
    min: getFactFromFactArray<number>(facts, `${factName}_min`, {
      [PlotReportFactLabelNameEnum.species]: species,
    })?.value,
    max: getFactFromFactArray<number>(facts, `${factName}_max`, {
      [PlotReportFactLabelNameEnum.species]: species,
    })?.value,
    value: getFactFromFactArray<number>(facts, factName, {
      [PlotReportFactLabelNameEnum.species]: species,
    })?.value,
  };
};

const getSpeciesFactsArray = (
  facts: PlotReportFactElement<unknown>[] | undefined,
  factName: R1FactType,
  species: SpeciesEnum,
): {
  min: PlotReportFactElement<number | null>[] | undefined;
  max: PlotReportFactElement<number | null>[] | undefined;
  value: PlotReportFactElement<number | null>[] | undefined;
} => {
  return {
    min: getFactsFromFactArray<number>(facts, `${factName}_min`, {
      [PlotReportFactLabelNameEnum.species]: species,
    }),
    max: getFactsFromFactArray<number>(facts, `${factName}_max`, {
      [PlotReportFactLabelNameEnum.species]: species,
    }),
    value: getFactsFromFactArray<number>(facts, factName, {
      [PlotReportFactLabelNameEnum.species]: species,
    }),
  };
};
